import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Per-DEVICE printer override, persisted browser-local (one workstation = one
 * browser). Configured in Settings → Printing → Defaults → This Device.
 *
 * When set, the chosen printer overrides the warehouse default for prints this
 * device triggers — manual pick-list / label prints AND the server-side
 * auto-print fired on picker assignment (the ids ride along as request headers;
 * see `devicePrinterHeaders`). It changes only WHICH printer a print lands on,
 * never the per-warehouse auto-print MODE (AUTO/ASK/OFF). `null` = no override,
 * fall back to the warehouse default.
 */
interface DevicePrinterState {
  /** Override for the label (ZPL) printer, or null to use the warehouse default. */
  labelPrinterId: string | null;
  /** Override for the document (pick-list PDF) printer, or null for the default. */
  documentPrinterId: string | null;
  setLabelPrinterId: (id: string | null) => void;
  setDocumentPrinterId: (id: string | null) => void;
}

export const useDevicePrinterStore = create<DevicePrinterState>()(
  persist(
    (set) => ({
      labelPrinterId: null,
      documentPrinterId: null,
      setLabelPrinterId: (id) => set({ labelPrinterId: id }),
      setDocumentPrinterId: (id) => set({ documentPrinterId: id }),
    }),
    { name: 'wms-device-printers' },
  ),
);

/**
 * Build the per-device printer override headers for a print-triggering request.
 * Attached only on the handful of calls that can print (assign-pickers + the
 * manual print endpoints) — NOT globally — so the override never rides on
 * unrelated traffic. Read imperatively from the store so it reflects the latest
 * selection at call time. Omits a header when its override is unset.
 */
export function devicePrinterHeaders(): Record<string, string> {
  const { labelPrinterId, documentPrinterId } =
    useDevicePrinterStore.getState();
  const headers: Record<string, string> = {};
  if (labelPrinterId) headers['x-label-printer-id'] = labelPrinterId;
  if (documentPrinterId) headers['x-document-printer-id'] = documentPrinterId;
  return headers;
}
